import React, { createContext, useContext, useState, useEffect } from "react";

const UserEmailContext = createContext();

/**
 * Provides a React context for managing the user's email address.
 * 
 * This provider component stores the user's email in both React state and localStorage,
 * ensuring persistence across page reloads. It exposes the current email and a setter function
 * to its children via context.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {React.ReactNode} props.children - The child components that will have access to the context.
 * @returns {JSX.Element} The provider component wrapping its children.
 *
 * @example
 * <UserEmailProvider>
 *   <YourComponent />
 * </UserEmailProvider>
 */
export const UserEmailProvider = ({ children }) => {
  const [userEmail, setUserEmail] = useState(() => {
    // Load the initial Email from localStorage
    return localStorage.getItem("userEmail") || null;
  });

  useEffect(() => {
    // Update localStorage whenever userEmail changes
    if (userEmail) {
      localStorage.setItem("userEmail", userEmail);
    } else {
      localStorage.removeItem("userEmail");
    }
  }, [userEmail]);

  return (
    <UserEmailContext.Provider value={{ userEmail, setUserEmail }}>
      {children}
    </UserEmailContext.Provider>
  );
};

export const useUserEmail = () => useContext(UserEmailContext);
